import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Paper,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import {
  selectSearchQuery,
  selectRecentSearches,
  setQuery,
  addRecentSearch,
} from '../../features/search/searchSlice';
import { searchService } from '../../services/search.service';
import { useTranslation } from 'react-i18next';

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  showRecentSearches?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder,
  showRecentSearches = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentQuery = useAppSelector(selectSearchQuery);
  const recentSearches = useAppSelector(selectRecentSearches);

  const [inputValue, setInputValue] = useState(currentQuery);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    setInputValue(currentQuery);
  }, [currentQuery]);

  // Fetch suggestions from API
  const generateSuggestions = async (query: string) => {
    if (!query.trim()) return;

    try {
      const suggestions = await searchService.getSuggestions(query, 5);
      setSuggestions(suggestions.map(s => s.text));
    } catch (error) {
      console.error('Failed to get suggestions:', error);

      // Fallback to mock suggestions if API fails
      const mockSuggestions = [
        'financial reports 2024',
        'budget planning documents',
        'quarterly review slides',
        'engineering documentation',
        'hr policies manual',
        'marketing strategy presentation',
        'project roadmap timeline',
        'api documentation guide',
        'design system guidelines',
        'security audit report',
      ];

      const filtered = mockSuggestions
        .filter(suggestion =>
          suggestion.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 5);

      setSuggestions(filtered);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    dispatch(setQuery(value));

    // Generate suggestions
    if (value.length > 0) {
      generateSuggestions(value);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearch = (searchQuery: string = inputValue) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;

    dispatch(addRecentSearch(trimmedQuery));
    setShowSuggestions(false);
    onSearch(trimmedQuery);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setInputValue('');
    dispatch(setQuery(''));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRecentSearchClick = (query: string) => {
    setInputValue(query);
    dispatch(setQuery(query));
    setShowSuggestions(false);
    onSearch(query);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    dispatch(setQuery(suggestion));
    handleSearch(suggestion);
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box position="relative" width="100%">
        <TextField
          fullWidth
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            if (inputValue.length > 0) {
              generateSuggestions(inputValue);
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder ?? t('documents.searchPlaceholder')}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: 'background.paper',
              },
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
              },
            },
          }}
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && (suggestions.length > 0 || recentSearches.length > 0) && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 300,
              overflow: 'auto',
              mt: 0.5,
            }}
          >
            {/* Recent Searches */}
            {showRecentSearches && recentSearches.length > 0 && inputValue.length === 0 && (
              <Box p={2}>
                <Box display="flex" alignItems="center" mb={1}>
                  <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('search.recentSearches')}
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {recentSearches.slice(0, 10).map((search, index) => (
                    <Chip
                      key={index}
                      label={search}
                      size="small"
                      variant="outlined"
                      onClick={() => handleRecentSearchClick(search)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <Box>
                {recentSearches.length > 0 && inputValue.length === 0 && <Box component="hr" sx={{ m: 0, border: 'none', borderTop: 1, borderColor: 'divider' }} />}
                <Box p={1}>
                  {suggestions.map((suggestion, index) => (
                    <Box
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {suggestion}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;
